import React from 'react';
import { EMZI_COLORS, EMZI_RADIUS, EMZI_SPACING } from '../../styles/emziCI';
import StructuredListInput from './StructuredListInput';
import { BILINGUAL_REQUIRED } from '../../utils/formValidation';

/**
 * EnhancedValuesForm - Values pack form with Input Quality Layer enhancements
 * Focus: Core principles, behavioral guidelines, brand values
 */
const EnhancedValuesForm = ({ 
  content = {}, 
  onChange, 
  errors = {}, 
  touched = {},
  onFieldBlur,
  packType = 'values'
}) => {
  const handleChange = (field, value) => {
    onChange({ ...content, [field]: value });
  };

  const requiresBilingual = BILINGUAL_REQUIRED[packType];

  return (
    <div style={{ 
      display: 'flex',
      flexDirection: 'column',
      gap: EMZI_SPACING.md,
    }}>
      {/* Core Principles - Structured List */}
      <StructuredListInput
        label="Core Principles"
        items={content.core_principles || []}
        onChange={(items) => handleChange('core_principles', items)}
        minItems={2}
        maxItems={10}
        placeholder="Add a core principle..."
        helper="Fundamental values that guide the brand. Example: 'Customer trust above all' or 'Transparency in every interaction'"
        required={true}
        hasError={touched.core_principles && errors.content?.core_principles}
        errorMessage={errors.content?.core_principles}
      />

      {/* Behavioral Guidelines - Structured List */}
      <StructuredListInput
        label="Behavioral Guidelines"
        items={content.behavioral_guidelines || []}
        onChange={(items) => handleChange('behavioral_guidelines', items)}
        minItems={2}
        maxItems={15}
        placeholder="Add a behavioral guideline..."
        helper="How team members should act. Example: 'Always greet customers warmly' or 'Listen before responding'"
        required={true}
        hasError={touched.behavioral_guidelines && errors.content?.behavioral_guidelines}
        errorMessage={errors.content?.behavioral_guidelines}
      />

      {/* Value Statement */}
      <div style={{ marginBottom: EMZI_SPACING.md }}>
        <label style={{
          display: 'block',
          fontSize: '14px',
          fontWeight: '500',
          marginBottom: EMZI_SPACING.xs,
          color: EMZI_COLORS.textPrimary,
        }}>
          Value Statement
          <span style={{
            fontSize: '11px',
            color: EMZI_COLORS.textMuted,
            marginLeft: EMZI_SPACING.xs,
            fontWeight: '400',
          }}>
            (Optional)
          </span>
        </label>
        
        <div style={{
          fontSize: '12px',
          color: EMZI_COLORS.textSecondary,
          marginBottom: EMZI_SPACING.xs,
          padding: EMZI_SPACING.xs,
          background: EMZI_COLORS.surface,
          borderRadius: EMZI_RADIUS.sm,
          borderLeft: `3px solid ${EMZI_COLORS.primary}`,
        }}>
          💡 A concise statement of this value. Example: "We believe in empowering our customers through education and transparency"
        </div>

        <textarea
          value={content.value_statement || ''}
          onChange={(e) => handleChange('value_statement', e.target.value)}
          onBlur={() => onFieldBlur && onFieldBlur('value_statement')}
          placeholder="e.g., We believe in empowering our customers through education and transparency"
          rows={3}
          style={{
            width: '100%',
            padding: EMZI_SPACING.sm,
            border: `1px solid ${EMZI_COLORS.border}`,
            borderRadius: EMZI_RADIUS.md,
            fontSize: '14px',
            fontFamily: 'inherit',
            outline: 'none',
            resize: 'vertical',
          }}
        />
      </div>

      {/* Expected Behaviors - Optional */}
      <StructuredListInput
        label="Expected Customer-Facing Behaviors"
        items={content.expected_behaviors || []}
        onChange={(items) => handleChange('expected_behaviors', items)}
        minItems={0}
        maxItems={10}
        placeholder="Add an expected behavior..."
        helper="Specific actions that demonstrate this value. Example: 'Acknowledge every customer within 30 seconds' or 'Provide alternatives if product is unavailable'"
        required={false}
        hasError={false}
      />

      {/* Prohibited Behaviors - Optional */}
      <StructuredListInput
        label="Prohibited Behaviors"
        items={content.prohibited_behaviors || []}
        onChange={(items) => handleChange('prohibited_behaviors', items)}
        minItems={0}
        maxItems={10}
        placeholder="Add a prohibited behavior..."
        helper="Actions that violate this value. Example: 'Never dismiss customer concerns' or 'No high-pressure sales tactics'"
        required={false}
        hasError={false}
      />

      {/* Real-World Examples - Optional */}
      <StructuredListInput
        label="Real-World Examples"
        items={content.examples || []}
        onChange={(items) => handleChange('examples', items)}
        minItems={0}
        maxItems={8}
        placeholder="Add an example scenario..."
        helper="Concrete examples of this value in action. Example: 'A customer complained about pricing - agent explained value proposition instead of offering discount'"
        required={false}
        hasError={false}
      />

      {/* Completion hint */}
      <div style={{
        padding: EMZI_SPACING.sm,
        background: '#e6f7ed',
        borderRadius: EMZI_RADIUS.sm,
        fontSize: '12px',
        color: EMZI_COLORS.success,
        borderLeft: `3px solid ${EMZI_COLORS.success}`,
      }}>
        ✅ <strong>Pro Tip:</strong> Values packs guide agent behavior and brand consistency. Be specific and actionable!
      </div>
    </div>
  );
};

export default EnhancedValuesForm;
